<template>
    <Modal
        size="lg"
        :okOnly="true"
        is-modal-route
        :centered="true"
        @okHandler="onSubmit"
        :no-close-on-esc="true"
        :no-close-on-backdrop="true"
        :ok-disabled="disabledOkButton"
        :hide-header-close="isSubmitting"
        :ok-title="$t('form.button.send')"
        :cancel-title="$t('modal.cancel')"
        :cancel-disabled="disabledCancelButton"
        :title="$t('form.button.sendFeedback')"
    >
        <div v-if="currentUserHasTalk">
            <Loader :loader="isModalLoading" />
            <div v-show="!isModalLoading">
                <b-form-group
                    :label="`${$t('form.label.wouldLikeToSend')}`"
                    label-for="select-user"
                >
                    <select-with-avatar
                        id="select-user"
                        :state="checkState('user')"
                        :options="usersOptions"
                        :selected="form.user"
                        @changeValue="setUser"
                        aria-describedby="evaluatedId"
                        :placeholder="$t('form.placeholder.sendFeedback')"
                        :close-on-select="true"
                        :internal-search="false"
                        @changeSearch="debouncedSearchHandler"
                        @next-page="nextPage"
                        :loading="loading"
                        :no-elements-found="
                            $t('form.multiselect.noUserWasFound')
                        "
                        :clear-on-select="true"
                        :next-page-button="nextPageButton"
                    />
                    <b-form-invalid-feedback id="evaluatedId">
                        <span v-if="!$v.form.user.required">
                            {{ $t('form.validations.users_required') }}
                        </span>
                    </b-form-invalid-feedback>
                </b-form-group>
                <category-select
                    :model.sync="$v.form.category.$model"
                    class="mb-3"
                />
                <b-form-group
                    label-for="text-editor"
                    :label="`${$t('form.label.writeYourFeedback')}`"
                >
                    <description
                        id="content"
                        ref="content"
                        :value.sync="$v.form.content.$model"
                        :placeholder="$t('form.placeholder.openTextArea')"
                        :toolbar="toolbar"
                        :state="checkState('content')"
                    />
                </b-form-group>
            </div>
        </div>
        <div v-else>
            <b-alert show variant="warning" class="mt-3">
                <i class="fa fa-warning"></i>
                {{ $t('notification.modules.noModuleTalk') }}
            </b-alert>
        </div>
        <template slot="okButton">
            <i v-if="isSubmitting" class="fa fa-spinner fa-spin px-2"></i>
        </template>
    </Modal>
</template>

<script>
// require styles
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import 'quill/dist/quill.bubble.css'

import { EventBuzz } from '@/main'
import Modal from '@/views/components/modal'
import Loader from '@/views/components/loader'
import SelectWithAvatar from '@/views/components/selects/select-with-avatar'
import { mapperToOptions as mapperToUsers } from '@/views/components/selects/select-with-avatar'

import { getUserData } from '@/services/user-data'
import { getUserPermissions } from '@/services/user-permissions'
import { getUser, getUsers, saveSendFeedback } from './api'

import notification, { success, danger } from '@/lib/notification'
import i18n from '@/i18n'

import { validationMixin } from 'vuelidate'
import { required, maxLength } from 'vuelidate/lib/validators'

import { sortBy, debounce } from 'lodash'
import Description from '../../../components/fields/Description.vue'
import { CategorySelect } from '@/views/talk/components'

notification.init()

const INITIAL_STATE = {
    user: {},
    content: '',
    category: null,
}

export default {
    name: 'SendFeedbackModal',
    components: {
        Modal,
        Loader,
        SelectWithAvatar,
        Description,
        CategorySelect,
    },
    mixins: [validationMixin],
    data() {
        return {
            userId: this.$store.state.currentUser.user.id,
            isSubmitting: false,
            usersReady: false,
            form: Object.assign({}, INITIAL_STATE),
            usersOptions: [],
            defaultUsers: [],
            showErrorMessage: false,
            loading: false,
            paginaton: {
                page: 1,
                lastPage: 1,
                perPage: 50,
            },
            search: null,
            isDefaultUsers: true,
            oldSearch: null,
            toolbar: [
                'bold',
                'italic',
                'underline',
                'strike',
                { list: 'ordered' },
                { list: 'bullet' },
            ],
        }
    },
    created() {
        this.debouncedSearchHandler = debounce(this.searchUserByName, 1000)
    },
    mounted() {
        if (this.currentUserHasTalk) {
            this.getUserFromApi()
        }
    },
    methods: {
        getUserFromApi() {
            getUser(this.userId).then(resp => {
                const {
                    data: { data },
                } = resp
                const subordinates = data.subordinates ? data.subordinates : []
                const manager = data.manager ? [data.manager] : []
                const user = [data]
                let defaultUsers = subordinates
                    .concat(manager)
                    .concat(user)
                    .filter(user => user.has_talk)
                defaultUsers = sortBy(defaultUsers, user => user.name)
                const users = mapperToUsers(defaultUsers)

                this.usersOptions = users.filter(user => {
                    if (
                        user.value != this.currentUserId &&
                        user.value == this.userId
                    ) {
                        this.setUser(user)
                    }
                    return user.value !== this.currentUserId
                })
                this.defaultUsers = this.usersOptions
                this.usersReady = true
            })
        },
        searchUserByName(search) {
            this.search = search
            if (search) {
                this.loading = true

                //Se for a primeira paginação
                if (this.isDefaultUsers || this.oldSearch != this.search) {
                    this.usersOptions = []
                    this.resetPagination()
                }

                const params = {
                    orderBy: 'name',
                    filter: 'id;name;occupation;picture',
                    search: `id:${this.currentUserId};name:${search.trim() ||
                        ''};has_talk:1`,
                    searchFields: 'id:!=;name:like;has_talk:=',
                    and: 'yes',
                    per_page: this.paginaton.perPage,
                    page: this.paginaton.page,
                }

                getUsers(params).then(resp => {
                    const {
                        data: { data },
                    } = resp
                    if (data.current_page > 1) {
                        this.usersOptions = this.usersOptions.concat(
                            mapperToUsers(data.data)
                        )
                    } else {
                        this.usersOptions = mapperToUsers(data.data)
                    }
                    this.paginaton.lastPage = data.last_page
                    this.loading = false
                    this.isDefaultUsers = false
                    this.oldSearch = search
                })
            } else {
                this.usersOptions = this.getDefaultUsers()
            }
        },
        getDefaultUsers() {
            this.isDefaultUsers = true
            this.resetPagination()
            return this.defaultUsers
        },
        resetPagination() {
            this.paginaton.page = 1
            this.paginaton.lastPage = 1
        },
        nextPage() {
            this.paginaton.page++
            this.searchUserByName(this.search)
        },
        checkState(val) {
            const field = this.$v.form[val]
            return field.$dirty ? !field.$invalid : null
        },
        validate() {
            this.$v.$touch()
            return this.isValid
        },
        onSubmit(event, closeModal) {
            event.preventDefault()
            if (!this.validate()) {
                return
            }
            this.isSubmitting = true
            this.$nextTick(() => {
                saveSendFeedback(
                    this.form.user.value,
                    this.form.content,
                    this.form.category
                )
                    .then(() => {
                        this.feedbackCreatedSuccessfully()
                        this.onReset
                    })
                    .catch(() => {
                        this.notificationError()
                    })
                    .finally(() => {
                        closeModal()
                        this.isSubmitting = false
                        EventBuzz.$emit('update-talk-summary')
                    })
            })
        },
        setUser(user) {
            this.$v.form.user.$touch()
            this.form.user = user
        },
        onReset() {
            this.form = Object.assign({}, INITIAL_STATE)
            this.$nextTick(() => {
                this.$v.$reset()
            })
        },
        feedbackCreatedSuccessfully() {
            success(
                i18n.t('notification.createdSuccessfully', {
                    item: i18n.t('feedbacks.feedback'),
                })
            )
        },
        notificationError() {
            danger()
        },
    },
    computed: {
        isModalLoading() {
            return !this.usersReady
        },
        disabledOkButton() {
            return this.isModalLoading || this.isSubmitting
        },
        disabledCancelButton() {
            return this.isSubmitting
        },
        isValid() {
            return !this.$v.form.$anyError
        },
        nextPageButton() {
            return {
                isShowNextPage:
                    this.paginaton.lastPage > 1 && !this.isDefaultUsers,
                isLastPage: this.paginaton.page == this.paginaton.lastPage,
                loading: this.loading,
            }
        },
        currentUserId() {
            const { id } = getUserData()
            return id
        },
        currentUserHasTalk() {
            const { modules } = getUserPermissions()
            return !!modules.has_talk
        },
    },
    validations: {
        form: {
            user: {
                required,
            },
            content: {
                required,
                maxLength: maxLength(65535),
            },
            category: {},
        },
    },
}
</script>

<style lang="sass" scoped>
::v-deep
    .modal-dialog
        display: flex
        justify-content: center
    .modal-header
        border: 0
        padding-bottom: 0
    .modal-title
        color: #5d9cec
        font-size: 20px
    .modal-body
        font-size: 15px
    .modal-footer
        border-top: 0
        padding-top: 0px
        button
            width: 78px
</style>
